// src/components/TaskViewModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Badge,
  Row,
  Col,
  ListGroup,
  Form,
  Alert,
  Spinner,
  Card,
  InputGroup,
  ProgressBar
} from 'react-bootstrap';
import TaskChat from './TaskChat';
import { useTasks } from '../contexts/TasksContext';
import { usersAPI, tasksAPI } from '../services/api.js';
import { formatters } from '../utils/formatters';
import { CONSTANTS } from '../constants';
import { useChat } from '../contexts/ChatContext';

const TaskViewModal = ({
  task,
  onClose,
  onEdit,
  onDelete,
  onComplete,
  onAssign,
  getStatusBadgeVariant,
  getPriorityBadgeVariant
}) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actualHours, setActualHours] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [loadingTask, setLoadingTask] = useState(true);
  const [currentTask, setCurrentTask] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const { addTaskComment, reloadTask } = useTasks();
  const { rooms } = useChat(); // используется только для логирования, не в зависимостях

  // Refs для безопасной работы с таймерами
  const mountedRef = useRef(true);
  const timersRef = useRef([]);

  // Очистка таймеров при размонтировании
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
    };
  }, []);

  // Безопасный setTimeout
  const setSafeTimeout = (callback, delay) => {
    const timer = setTimeout(() => {
      if (mountedRef.current) {
        callback();
      }
    }, delay);
    timersRef.current.push(timer);
    return timer;
  };

  // Загрузка данных задачи при открытии модального окна
  useEffect(() => {
    if (task?.id) {
      console.log('🔄 Initial loading task data for modal:', task.id);
      setLoadingTask(true);

      reloadTask(task.id)
        .then(result => {
          if (result.success && mountedRef.current) {
            console.log('✅ Task data loaded:', result.task);
            console.log('🔍 CHAT_ROOM_ID:', result.task.chat_room_id);
            console.log('🔍 Task rooms:', rooms?.map(r => ({id: r.id, task_id: r.task_id})));
            setCurrentTask(result.task);
          } else if (mountedRef.current) {
            setCurrentTask(task);
          }
        })
        .catch(err => {
          console.error('❌ Error loading task data:', err);
          if (mountedRef.current) {
            setCurrentTask(task);
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setLoadingTask(false);
          }
        });
    }
  }, [task?.id, reloadTask]);// убрали rooms из зависимостей

  // Сбрасываем состояние когда модальное окно закрывается
  useEffect(() => {
    if (!task && mountedRef.current) {
      setCurrentTask(null);
      setLoadingTask(true);
      setActiveTab('details');
      setSelectedFile(null);
      setUploadError('');
      setUploadProgress(0);
    }
  }, [task]);

  useEffect(() => {
    if (showAssignModal) {
      loadUsers();
    }
  }, [showAssignModal]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await usersAPI.getUsers({ limit: 100 });
      const usersList = response.users || response.data || response;
      if (mountedRef.current) {
        setUsers(Array.isArray(usersList) ? usersList : []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      if (mountedRef.current) {
        setUsers([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoadingUsers(false);
      }
    }
  };

  const displayTask = currentTask || task;

  // Обработчик выбора файла
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > CONSTANTS.LIMITS.FILE_SIZE_MAX) {
        setUploadError('Размер файла не должен превышать 10MB');
        setSelectedFile(null);
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setUploadError('');
    }
  };

  // Обработчик загрузки файла
  const handleFileUpload = async () => {
    if (!selectedFile || !displayTask?.id) return;

    setUploadingFile(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      console.log('📤 Uploading file:', selectedFile.name, 'to task:', displayTask.id);

      const onUploadProgress = (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (mountedRef.current) {
          setUploadProgress(progress);
        }
      };

      await tasksAPI.addAttachment(displayTask.id, selectedFile, onUploadProgress);
      console.log('✅ File uploaded successfully');

      if (mountedRef.current) {
        setSelectedFile(null);
        setUploadProgress(100);
      }

      // Перезагружаем задачу для получения обновленного списка файлов
      setSafeTimeout(async () => {
        if (mountedRef.current) {
          const reloadResult = await reloadTask(displayTask.id);
          if (reloadResult.success && mountedRef.current) {
            setCurrentTask(reloadResult.task);
          }
          setUploadingFile(false);
          setUploadProgress(0);
          setUploadError('');
        }
      }, 1000);

    } catch (err) {
      console.error('❌ Error uploading file:', err);

      if (mountedRef.current) {
        if (err.response?.status === 422) {
          const errorDetail = err.response?.data?.detail;
          if (Array.isArray(errorDetail)) {
            const errorMessages = errorDetail.map(err => {
              if (err.loc && err.msg) {
                return `${err.loc.join('.')}: ${err.msg}`;
              }
              return err.msg || JSON.stringify(err);
            }).join('; ');
            setUploadError(`Ошибка валидации: ${errorMessages}`);
          } else if (typeof errorDetail === 'string') {
            setUploadError(errorDetail);
          } else {
            setUploadError('Неверный формат файла или параметры загрузки');
          }
        } else {
          setUploadError(err.response?.data?.detail || err.message || 'Не удалось загрузить файл');
        }

        setUploadingFile(false);
        setUploadProgress(0);
      }
    }
  };

  // Обработчик скачивания файла
  const handleFileDownload = async (attachment) => {
    if (!displayTask?.id || !attachment?.id) return;

    try {
      console.log('📥 Downloading file:', attachment.filename);

      const blob = await tasksAPI.downloadAttachment(displayTask.id, attachment.id);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename || 'file';
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error('❌ Error downloading file:', err);
      alert('Не удалось скачать файл: ' + (err.message || 'Ошибка сети'));
    }
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !displayTask?.id) return;

    try {
      setAddingComment(true);
      console.log('📤 Adding comment to task:', displayTask.id, newComment);

      const result = await addTaskComment(displayTask.id, newComment);

      if (result.success && mountedRef.current) {
        setNewComment('');
        console.log('✅ Comment added successfully');
        const reloadResult = await reloadTask(displayTask.id);
        if (reloadResult.success && mountedRef.current) {
          setCurrentTask(reloadResult.task);
        }
      } else if (mountedRef.current) {
        alert('Не удалось добавить комментарий: ' + result.error);
      }
    } catch (err) {
      console.error('❌ Error adding comment:', err);
      if (mountedRef.current) {
        alert('Не удалось добавить комментарий: ' + (err.message || 'Ошибка сети'));
      }
    } finally {
      if (mountedRef.current) {
        setAddingComment(false);
      }
    }
  };

  const handleComplete = () => {
    if (actualHours) {
      onComplete(displayTask.id, parseInt(actualHours));
    } else {
      onComplete(displayTask.id);
    }
    setShowCompleteModal(false);
    setActualHours('');

    setTimeout(() => {
      const completeModal = document.querySelector('.modal.show');
      if (completeModal) {
        const closeBtn = completeModal.querySelector('.btn-close');
        if (closeBtn) closeBtn.click();
      }
    }, 100);
  };

  const handleAssign = () => {
    if (assigneeId) {
      onAssign(displayTask.id, parseInt(assigneeId));
      setShowAssignModal(false);
      setAssigneeId('');

      setTimeout(() => {
        const assignModal = document.querySelector('.modal.show');
        if (assignModal) {
          const closeBtn = assignModal.querySelector('.btn-close');
          if (closeBtn) closeBtn.click();
        }
      }, 100);
    }
  };

  const handleDelete = () => {
    onDelete(displayTask.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCommentDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => formatters.formatTaskStatus(status);
  const getPriorityText = (priority) => formatters.formatTaskPriority(priority);
  const getCategoryText = (category) => formatters.formatTaskCategory(category);
  const getFullName = (user) => formatters.getUserDisplayName(user);

  const isSystemComment = (comment) => {
    return comment.is_system || comment.content?.includes('Задача') || comment.user_id === null;
  };

  const getCommentAuthorName = (comment) => {
    if (isSystemComment(comment)) {
      return 'Система';
    }
    return comment.author_name || comment.author?.name || 'Пользователь';
  };

  const getCommentIcon = (comment) => {
    if (isSystemComment(comment)) {
      return 'bi-gear-fill text-primary';
    }
    return 'bi-person-fill text-secondary';
  };

  const getCommentText = (comment) => {
    return comment.content || comment.text || '';
  };

  const isOverdue = displayTask && displayTask.due_date && new Date(displayTask.due_date) < new Date() &&
                   !['completed', 'cancelled'].includes(displayTask.status);

  const canEdit = displayTask && displayTask.status !== 'completed';

  if (!task) return null;

  return (
    <>
      {/* Основное модальное окно просмотра задачи */}
      <Modal show={!!task} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <span>Задача #{displayTask.id}</span>
              <Badge bg={getStatusBadgeVariant(displayTask.status)}>
                {getStatusText(displayTask.status)}
              </Badge>
              <Badge bg={getPriorityBadgeVariant(displayTask.priority)}>
                {getPriorityText(displayTask.priority)}
              </Badge>
              {isOverdue && (
                <Badge bg="danger">
                  <i className="bi bi-clock me-1"></i>
                  Просрочена
                </Badge>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loadingTask && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Загрузка данных...</span>
            </div>
          )}

          <div className="mb-4">
            <div className="nav nav-tabs">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
                disabled={loadingTask}
              >
                <i className="bi bi-info-circle me-2"></i>
                Детали задачи
              </button>
              <button
                className={`nav-link ${activeTab === 'comments' ? 'active' : ''}`}
                onClick={() => setActiveTab('comments')}
                disabled={loadingTask}
              >
                <i className="bi bi-chat-dots me-2"></i>
                Комментарии
                {displayTask.comments && displayTask.comments.length > 0 && (
                  <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                    {displayTask.comments.length}
                  </Badge>
                )}
              </button>
              <button
                className={`nav-link ${activeTab === 'files' ? 'active' : ''}`}
                onClick={() => setActiveTab('files')}
                disabled={loadingTask}
              >
                <i className="bi bi-paperclip me-2"></i>
                Файлы
                {displayTask.attachments && displayTask.attachments.length > 0 && (
                  <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                    {displayTask.attachments.length}
                  </Badge>
                )}
              </button>
              <button
                className={`nav-link ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => setActiveTab('chat')}
                disabled={loadingTask}
              >
                <i className="bi bi-chat-dots me-2"></i>
                Чат
                {/* {displayTask.chat_room_id && (
                  <Badge bg="primary" className="ms-1" style={{ fontSize: '0.6rem' }}>
                    <i className="bi bi-chat"></i>
                  </Badge>
                )} */}
              </button>
            </div>
          </div>

          {activeTab === 'details' ? (
            <Row className="g-4">
              <Col md={8}>
                <div className="mb-4">
                  <h4 className="mb-3">{displayTask.title}</h4>
                  {displayTask.description && (
                    <div className="bg-light p-3 rounded mb-3">
                      <h6 className="text-muted mb-2">Описание:</h6>
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                        {displayTask.description}
                      </p>
                    </div>
                  )}
                </div>

                <ListGroup variant="flush">
                  <ListGroup.Item className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">Категория:</span>
                    <span className="fw-semibold">{getCategoryText(displayTask.category)}</span>
                  </ListGroup.Item>

                  {displayTask.estimated_hours && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Оценка времени:</span>
                      <span className="fw-semibold">{displayTask.estimated_hours} часов</span>
                    </ListGroup.Item>
                  )}

                  {displayTask.actual_hours && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Фактическое время:</span>
                      <span className="fw-semibold text-success">{displayTask.actual_hours} часов</span>
                    </ListGroup.Item>
                  )}

                  {displayTask.due_date && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Срок выполнения:</span>
                      <span className={`fw-semibold ${isOverdue ? 'text-danger' : ''}`}>
                        {formatDate(displayTask.due_date)}
                      </span>
                    </ListGroup.Item>
                  )}

                  {displayTask.location && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Местоположение:</span>
                      <span className="fw-semibold">{displayTask.location}</span>
                    </ListGroup.Item>
                  )}

                  {displayTask.contact_info && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Контактная информация:</span>
                      <span className="fw-semibold">{displayTask.contact_info}</span>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Col>

              <Col md={4}>
                <div className="bg-light rounded p-3">
                  <h6 className="border-bottom pb-2 mb-3">Метаданные</h6>

                  {/* Создатель */}
                  <div className="mb-3">
                    <small className="text-muted">Создатель:</small>
                    <div className="fw-semibold">
                      {getFullName(displayTask.creator)}
                    </div>
                    {/* ✅ ДОБАВИТЬ ТЕЛЕФОН СОЗДАТЕЛЯ */}
                    {displayTask.creator?.phone && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-telephone me-1"></i>
                          <a href={`tel:${displayTask.creator.phone}`} className="text-decoration-none">
                            {formatters.formatPhone(displayTask.creator.phone)}
                          </a>
                      </div>
                    )}
                  </div>

                  {/* Исполнитель */}
                  <div className="mb-3">
                    <small className="text-muted">Исполнитель:</small>
                    <div className="fw-semibold">
                      {getFullName(displayTask.assignee)}
                    </div>
                    {/* ✅ ДОБАВИТЬ ТЕЛЕФОН ИСПОЛНИТЕЛЯ */}
                    {displayTask.assignee?.phone && (
                      <div className="small text-muted mt-1">
                        <i className="bi bi-telephone me-1"></i>
                          <a href={`tel:${displayTask.assignee.phone}`} className="text-decoration-none">
                            {formatters.formatPhone(displayTask.assignee.phone)}
                          </a>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">Дата создания:</small>
                    <div className="fw-semibold">{formatDate(displayTask.created_at)}</div>
                  </div>

                  {displayTask.updated_at && (
                    <div className="mb-3">
                      <small className="text-muted">Последнее обновление:</small>
                      <div className="fw-semibold">{formatDate(displayTask.updated_at)}</div>
                    </div>
                  )}

                  {displayTask.completed_at && (
                    <div className="mb-3">
                      <small className="text-muted">Дата завершения:</small>
                      <div className="fw-semibold text-success">{formatDate(displayTask.completed_at)}</div>
                    </div>
                  )}

                  {displayTask.device_info && Object.keys(displayTask.device_info).length > 0 && (
                    <div className="mb-3">
                      <small className="text-muted">Информация об устройстве:</small>
                      <pre className="small bg-white p-2 rounded mt-1">
                        {JSON.stringify(displayTask.device_info, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          ) : activeTab === 'comments' ? (
            <div>
              <Card className="mb-4">
                <Card.Body>
                  <h6 className="mb-3">Добавить комментарий</h6>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Введите ваш комментарий..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                      disabled={addingComment || loadingTask}
                    />
                    <Button
                      variant="primary"
                      onClick={handleAddComment}
                      disabled={addingComment || !newComment.trim() || loadingTask}
                    >
                      {addingComment ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Отправить'
                      )}
                    </Button>
                  </InputGroup>
                </Card.Body>
              </Card>

              {loadingTask ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Загрузка комментариев...</span>
                </div>
              ) : (!displayTask.comments || displayTask.comments.length === 0) ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-chat-square-text display-4 mb-3 opacity-50"></i>
                  <h5>Комментариев пока нет</h5>
                  <p>Будьте первым, кто оставит комментарий</p>
                </div>
              ) : (
                <div className="comments-list">
                  {displayTask.comments.map(comment => (
                    <Card
                      key={comment.id}
                      className={`mb-3 ${isSystemComment(comment) ? 'border-primary bg-light' : ''}`}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center">
                            <i className={`bi ${getCommentIcon(comment)} me-2`}></i>
                            <div className="fw-semibold small">
                              {getCommentAuthorName(comment)}
                              {isSystemComment(comment) && (
                                <Badge bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>
                                  системный
                                </Badge>
                              )}
                            </div>
                          </div>
                          <small className="text-muted">
                            {formatCommentDate(comment.created_at)}
                          </small>
                        </div>
                        <p
                          className="mb-0 small"
                          style={{
                            whiteSpace: 'pre-wrap',
                            lineHeight: '1.4',
                            fontSize: '0.875rem',
                            color: isSystemComment(comment) ? '#495057' : '#212529'
                          }}
                        >
                          {getCommentText(comment)}
                        </p>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'files' ? (
            <div>
              <Card className="mb-4">
                <Card.Body>
                  <h6 className="mb-3">Загрузить файл</h6>

                  <div className="mb-3">
                    <Form.Control
                      type="file"
                      onChange={handleFileSelect}
                      disabled={uploadingFile || loadingTask}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.zip,.rar"
                    />
                    <Form.Text className="text-muted">
                      Поддерживаемые форматы: PDF, DOC, XLS, JPG, PNG, TXT, ZIP, RAR (макс. 10MB)
                    </Form.Text>
                  </div>

                  {selectedFile && (
                    <Alert variant="info" className="py-2 mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <i className="bi bi-file-earmark me-2"></i>
                          <strong>{selectedFile.name}</strong>
                          <span className="ms-2 text-muted">
                            ({formatters.formatFileSize(selectedFile.size)})
                          </span>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setSelectedFile(null)}
                        >
                          <i className="bi bi-x"></i>
                        </Button>
                      </div>
                    </Alert>
                  )}

                  {uploadError && (
                    <Alert variant="danger" className="py-2 mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {uploadError}
                    </Alert>
                  )}

                  {uploadingFile && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">Загрузка...</small>
                        <small className="text-muted">{uploadProgress}%</small>
                      </div>
                      <ProgressBar
                        now={uploadProgress}
                        animated
                        striped
                        variant="primary"
                      />
                    </div>
                  )}

                  <Button
                    variant="primary"
                    onClick={handleFileUpload}
                    disabled={!selectedFile || uploadingFile || loadingTask}
                  >
                    {uploadingFile ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Загрузка...
                      </>
                    ) : (
                      'Загрузить файл'
                    )}
                  </Button>
                </Card.Body>
              </Card>

              <h6 className="mb-3">Прикрепленные файлы</h6>

              {loadingTask ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Загрузка файлов...</span>
                </div>
              ) : (!displayTask.attachments || displayTask.attachments.length === 0) ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-folder-x display-4 mb-3 opacity-50"></i>
                  <h5>Файлы отсутствуют</h5>
                  <p>Загрузите первый файл</p>
                </div>
              ) : (
                <div className="attachments-list">
                  {displayTask.attachments.map(attachment => (
                    <Card key={attachment.id} className="mb-3">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <i className={`bi ${formatters.getFileIcon(attachment.filename)} fs-3 text-primary`}></i>
                            </div>
                            <div>
                              <h6 className="mb-1">
                                <span
                                  className="text-primary cursor-pointer hover-underline"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleFileDownload(attachment)}
                                  title="Нажмите для скачивания"
                                >
                                  {attachment.filename}
                                </span>
                              </h6>
                              <div className="small text-muted">
                                <span className="me-3">
                                  <i className="bi bi-hdd me-1"></i>
                                  {formatters.formatFileSize(attachment.file_size)}
                                </span>
                                <span>
                                  <i className="bi bi-clock me-1"></i>
                                  {formatFileDate(attachment.uploaded_at)}
                                </span>
                                {attachment.mime_type && (
                                  <span className="ms-3">
                                    <i className="bi bi-filetype-doc me-1"></i>
                                    {attachment.mime_type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleFileDownload(attachment)}
                              title="Скачать файл"
                            >
                              <i className="bi bi-download"></i>
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}

              {displayTask.attachments && displayTask.attachments.length > 0 && (
                <div className="mt-3 text-center text-muted small">
                  <i className="bi bi-info-circle me-1"></i>
                  Всего файлов: <strong>{displayTask.attachments.length}</strong> •
                  Общий размер: <strong>
                    {formatters.formatFileSize(displayTask.attachments.reduce((total, file) => total + file.file_size, 0))}
                  </strong>
                </div>
              )}
            </div>
          ) : activeTab === 'chat' && displayTask?.chat_room_id ? (
            <TaskChat taskId={displayTask.id} taskTitle={displayTask.title} />
          ) : activeTab === 'chat' && !displayTask?.chat_room_id ? (
            <Alert variant="info">
              <i className="bi bi-info-circle me-2"></i>
              Для этой задачи чат еще не создан. Чат будет доступен после создания задачи.
            </Alert>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {canEdit && (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={() => onEdit(displayTask)}
                    className="me-2"
                    disabled={loadingTask}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Редактировать
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={() => setShowCompleteModal(true)}
                    className="me-2"
                    disabled={loadingTask}
                  >
                    <i className="bi bi-check-lg me-1"></i>
                    Завершить
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={() => setShowAssignModal(true)}
                    className="me-2"
                    disabled={loadingTask}
                  >
                    <i className="bi bi-person-plus me-1"></i>
                    Назначить
                  </Button>
                </>
              )}
            </div>
            <div>
              <Button
                variant="outline-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loadingTask}
              >
                <i className="bi bi-trash me-1"></i>
                Удалить
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно завершения задачи */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check-circle me-2 text-success"></i>
            Завершение задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Вы уверены, что хотите завершить задачу "<strong>{displayTask.title}</strong>"?</p>

          <Form.Group>
            <Form.Label>Фактическое время выполнения (часы)</Form.Label>
            <Form.Control
              type="number"
              value={actualHours}
              onChange={(e) => setActualHours(e.target.value)}
              placeholder="Введите фактическое время"
              min="1"
              max="1000"
            />
            <Form.Text className="text-muted">
              Оставьте пустым, если не хотите указывать
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
            Отмена
          </Button>
          <Button variant="success" onClick={handleComplete}>
            <i className="bi bi-check-lg me-2"></i>
            Завершить задачу
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно назначения задачи */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-plus me-2 text-info"></i>
            Назначение задачи
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Назначить задачу "<strong>{displayTask.title}</strong>" пользователю:</p>

          <Form.Group>
            <Form.Label>Выберите пользователя</Form.Label>
            {loadingUsers ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Загрузка пользователей...</span>
              </div>
            ) : (
              <Form.Select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                required
              >
                <option value="">Выберите исполнителя</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {getFullName(user)} (ID: {user.id})
                    {user.telegram_username && ` - @${user.telegram_username}`}
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              Выберите пользователя, которому хотите назначить задачу
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!assigneeId}
          >
            <i className="bi bi-person-check me-2"></i>
            Назначить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно подтверждения удаления */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Подтверждение удаления
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Вы точно хотите удалить задачу "<strong>{displayTask.title}</strong>"?</p>
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Это действие невозможно отменить. Все данные задачи будут удалены.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-2"></i>
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskViewModal;